import services from './services';
import {
  MATCH_STATUS_TYPES,
  filterInitialState
} from '@/store/modules/matches';
import { lbApiBeta } from '../../../utils/axiosConfig';

async function insertMockedMatchRequest(
  context,
  { propositionId, proposition2Id }
) {
  const primaryPropositionId =
    context.rootState.myPropositions.primaryProposition.propositionId;

  const mockedChatRoom = await services.mockChatRoom(
    primaryPropositionId,
    propositionId,
    proposition2Id
  );

  if (!mockedChatRoom || mockedChatRoom.id !== 0) {
    return;
  }

  context.commit('setMatchRequests', [
    mockedChatRoom,
    ...(context.state.matchRequests?.rooms.filter(r => r.id > 0) || []) // Exclude existing mocked chatroom
  ]);
}

// silent: reload without the skeleton, keeping the list on screen.
async function fetchMatchRequests(
  context,
  { currentSize = 0, chatId = null, silent = false }
) {
  const showSpinner = currentSize === 0 && !silent;
  if (showSpinner) {
    context.commit('setLoadingMatchRequests', { isLoadingMatchRequests: true });
  }

  try {
    const matchRequests = await services.fetchMatchRequests(
      context.rootState.myPropositions.primaryProposition.propositionId,
      currentSize,
      chatId == 0 ? null : chatId,
      context.state.filter
    );

    // The service resolves undefined when the request failed: keep what is
    // loaded, but make sure the views always get a list to read (KL-V/KL-12).
    if (!matchRequests) {
      if (!context.state.matchRequests) context.commit('setMatchRequests', []);
      return { matchRequests: null };
    }

    const rooms = matchRequests.rooms || [];
    if (currentSize === 0) {
      context.commit('setMatchRequests', rooms);
    } else {
      context.commit('appendMatchRequests', rooms);
    }
    context.commit('setMatchRequestsFetchedCount', currentSize + rooms.length);

    context.commit('setHasMore', !!matchRequests.hasMore);

    return { matchRequests };
  } finally {
    if (showSpinner) {
      context.commit('setLoadingMatchRequests', {
        isLoadingMatchRequests: false
      });
    }
  }
}

async function getMatches(context, { currentSize = 0, matchId } = {}) {
  if (currentSize === 0) {
    context.commit('setLoadingMatches', { isLoadingMatches: true });
  }

  const response = await services.fetchMatches(
    context.rootState.myPropositions.primaryProposition.propositionId,
    context.state.filter,
    currentSize,
    matchId
  );

  const matches = response?.matches;

  // A failed request resolves undefined: keep the loaded list rather than
  // replacing it with undefined (KL-2B) or spreading undefined into it (KL-29).
  if (Array.isArray(matches)) {
    if (currentSize === 0) {
      context.commit('setMatches', { matches });
    } else {
      context.commit('appendMatches', { matches });
    }
    context.commit('setMatchesHasMore', !!response.hasMore);
  } else if (!context.state.matches.active) {
    context.commit('setMatches', { matches: [] });
  }

  if (currentSize === 0) {
    context.commit('setLoadingMatches', { isLoadingMatches: false });
  }
}

async function refetchSingleMatch(context, { myPropositionId, matchId }) {
  const match = await services.refetchSingleMatch(myPropositionId, matchId);

  // Loop through all matches and replace the old match(es) with the new refetched one
  const allMatches = Object.values(context.state.matches);
  allMatches.forEach(matchesInTab => {
    if (matchesInTab === null) return;
    // Find the index of the refetched match(es)
    const matchIndex = matchesInTab.findIndex(match => match.id === matchId);

    // Replace the old match(es) with the new refetched one
    if (matchIndex !== -1) {
      const matches = [...matchesInTab];
      matches.splice(matchIndex, 1, match);
      context.commit('setMatches', { matches });
    }
  });

  return match;
}

async function setActiveTab(
  context,
  { activeTab, refetchStatusCounts = false, matchId }
) {
  context.commit('setActiveTab', activeTab);

  await context.dispatch('getMatches', { matchId });

  if (refetchStatusCounts) {
    const updatedCounts = { ...context.state.matchStatusCounts };
    updatedCounts[activeTab] = context.state.matches[activeTab]?.length;
    context.commit('setMatchStatusCounts', updatedCounts);
  }
}

async function getInitialMatchStatusCounts(context) {
  const summary = await services.fetchInitialMatchStatusCounts(
    context.rootState.myPropositions.primaryProposition.propositionId
  );

  context.commit('setInitialMatchStatusCounts', summary);

  return summary;
}

async function init(context, { matchId }) {
  context.commit('setLoadingStatusCounts', { isLoadingStatusCounts: true });

  context.commit('resetMatches');

  await getInitialMatchStatusCounts(context);
  context.commit(
    'setMatchStatusCounts',
    context.state.initialMatchStatusCounts
  );

  setActiveTab(context, { activeTab: MATCH_STATUS_TYPES.ACTIVE, matchId });

  context.commit('setLoadingStatusCounts', { isLoadingStatusCounts: false });
}

// Variant of `init` for the matches2 view which has no filter UI and
// uses its own v2 fetches — skips the setActiveTab/getMatches chain
// (and thus the filter-aware payload) entirely.
async function initStatusCountsOnly(context) {
  context.commit('setLoadingStatusCounts', { isLoadingStatusCounts: true });

  await getInitialMatchStatusCounts(context);
  context.commit(
    'setMatchStatusCounts',
    context.state.initialMatchStatusCounts
  );

  context.commit('setLoadingStatusCounts', { isLoadingStatusCounts: false });
}

function setNewMatch(context, match) {
  context.commit('setNewMatch', match);
}

async function leaveRoom(_, userConversationId) {
  await lbApiBeta.delete(`/api/messenger/chat-room/${userConversationId}`);
}

async function confirmDeleteRequest(
  { state, commit, dispatch },
  { requestId, conversationId, selectedRequestId }
) {
  if (!requestId || !conversationId) {
    return { ok: false, reason: 'missing_params' };
  }

  const rooms = state.matchRequests?.rooms || [];
  const idx = rooms.findIndex(x => String(x.id) === String(requestId));
  if (idx === -1) return { ok: false, reason: 'not_found' };

  try {
    await dispatch('leaveRoom', conversationId);
  } catch (error) {
    return { ok: false, error };
  }

  commit('removeMatchRequest', requestId);

  const clearedSelected = String(selectedRequestId) === String(requestId);
  return { ok: true, clearedSelected };
}

function setFilterAndUpdateQuery(context, { filterValue, router }) {
  context.commit('setFilterValue', filterValue);

  if (router) {
    const query = { ...router.currentRoute.query };

    // Add filter values to query
    Object.keys(filterValue).forEach(key => {
      if (filterValue[key] !== filterInitialState[key]) {
        query[`filter_${key}`] = filterValue[key];
      } else {
        delete query[`filter_${key}`];
      }
    });

    router.replace({ query });
  }
}

function loadFilterFromQuery(context, query) {
  const filterValue = {};

  // Get filter values from query parameter
  Object.keys(query).forEach(key => {
    if (key.startsWith('filter_')) {
      const filterKey = key.replace('filter_', '');
      if (filterKey in filterInitialState) {
        let value = query[key];

        // Convert string values to booleans
        if (value === 'true') value = true;
        if (value === 'false') value = false;

        filterValue[filterKey] = value;
      }
    }
  });

  // Only commit if we have filters set
  if (Object.keys(filterValue).length > 0) {
    context.commit('setFilterValue', filterValue);
  }
}

function resetFilterAndQuery(context, router) {
  context.commit('resetFilter');

  if (
    router &&
    Object.keys(router.history.current.query).some(key =>
      key.includes('filter_')
    )
  ) {
    // Remove filter query parameters
    const query = { ...router.currentRoute.query };

    Object.keys(query).forEach(key => {
      if (key.startsWith('filter_')) {
        delete query[key];
      }
    });

    router.replace({ query });
  }
}

export default {
  getMatches,
  insertMockedMatchRequest,
  refetchSingleMatch,
  setActiveTab,
  getInitialMatchStatusCounts,
  setNewMatch,
  init,
  initStatusCountsOnly,
  fetchMatchRequests,
  leaveRoom,
  confirmDeleteRequest,
  setFilterAndUpdateQuery,
  loadFilterFromQuery,
  resetFilterAndQuery
};
